/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import React, { useEffect, useState } from "react";
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { LocationState, PickupFormData } from "../../../types/types";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
} from "../../../apis/locationApis";
import NewinputField from "../../../components/NewInput";
import PickupAddressPicker from "./PickupAddressPicker";
import { IconLocation } from "@tabler/icons-react";

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 mb-3">
    <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">
      {title}
    </p>
  </div>
);

interface CreateLocationProps {
  register: UseFormRegister<PickupFormData>;
  setValue: UseFormSetValue<PickupFormData>;
  watch: UseFormWatch<PickupFormData>;
  errors: FieldErrors<PickupFormData>;
  clearErrors: UseFormClearErrors<PickupFormData>;
  control: Control<PickupFormData>;
  reset?: any;
  className?: string;
  mode?: string;
  defaultValues?: PickupFormData;
}
const CreateLocation: React.FC<CreateLocationProps> = ({
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  control,
  mode,
  defaultValues,
  reset,
}) => {
  const [createLocation, setCreateLocation] = useState<LocationState>({});
  const { data: countries = [] } = useFetchCountriesAllData();

  const {
    data: cities = [],
    refetch: fetchReceiverCities,
    isFetching: isFetchingCities,
  } = useFetchCountryCitiesAllData(createLocation?.country?.id);

  const countryId = watch("countryId");
  const cityId = watch("cityId");
  const districtId = watch("districtId");

  const isView = mode === "view";

  useEffect(() => {
    if (createLocation?.country?.id) {
      fetchReceiverCities();
    }
  }, [createLocation?.country?.id]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
      const def = defaultValues as any;
      setCreateLocation({
        country: def.countryId ? { id: def.countryId, name: def.country } : undefined,
        city: def.cityId ? { id: def.cityId, name: def.city } : undefined,
        district: def.districtId ? { id: def.districtId, name: def.district } : undefined,
      });
    } else {
      reset({});
      setCreateLocation({});
    }
  }, [defaultValues, reset]);

  // Sync country selection change / loading from list
  useEffect(() => {
    if (countryId && countries.length > 0) {
      const matchedCountry = countries.find((c: any) => c.id === countryId);
      if (matchedCountry) {
        setCreateLocation((prev) => {
          if (prev.country?.id === matchedCountry.id) return prev;
          return { ...prev, country: { id: matchedCountry.id, name: matchedCountry.name } };
        });
      }
    }
  }, [countryId, countries]);

  // Sync city selection change / loading from list
  useEffect(() => {
    if (cityId && cities.length > 0) {
      const matchedCity = cities.find((c: any) => c.id === cityId);
      if (matchedCity) {
        setCreateLocation((prev) => {
          if (prev.city?.id === matchedCity.id) return prev;
          return { ...prev, city: { id: matchedCity.id, name: matchedCity.name } };
        });
      }
    }
  }, [cityId, cities]);

  return (
    <div className="w-full p-6 space-y-4 bg-transparent">
      <NewinputField
        className="flex flex-col gap-1"
        name="locationName"
        type="text"
        title="Location Name"
        placeholder="Enter Location Name"
        register={register}
        error={errors.locationName}
        validation={{
          required: "Location Name is required",
          pattern: {
            value: /^(?=.*[A-Za-z])[A-Za-z0-9\s-]+$/,
            message:
              "Location Name must include at least one letter (not only numbers)",
          },
          validate: (value) => {
            if (!value?.trim())
              return "Location Name cannot be empty or only spaces";
            return true;
          },
        }}
        disabled={isView}
      />

      <PickupAddressPicker
        register={register}
        errors={errors}
        setValue={setValue}
        control={control as Control<any>}
        clearErrors={clearErrors}
        countries={countries}
        cities={cities}
        selectedCountry={createLocation.country}
        selectedCity={createLocation.city}
        selectedDistrict={createLocation.district}
        onCountryChange={(country) => setCreateLocation(prev => ({ ...prev, country, city: undefined, district: undefined }))}
        onCityChange={(city) => setCreateLocation(prev => ({ ...prev, city, district: undefined }))}
        onDistrictChange={(district) => setCreateLocation(prev => ({ ...prev, district }))}
        mode={mode}
      />
    </div>
  );
};

export default CreateLocation;
